import {
  Alert,
  Anchor,
  Autocomplete,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconTruck } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { CARRIERS, trackingUrl, usePermission } from "@how/app-kit";
import { EmptyState, ErrorState, LoadingState, PageHeader, useReturnFocus } from "@how/ui";
import { formatWhen } from "../events/event-queries";
import { RefundDialog } from "./RefundDialog";
import { StoreTabs } from "./StoreTabs";
import { UndoPickupDialog } from "./UndoPickupDialog";
import {
  addressLines,
  chargedCents,
  formatMoney,
  ORDER_STATUS_COLOR,
  orderStatusLabel,
  type OrderRow,
  type OrderStatus,
  useCancelOrder,
  useFulfilOrder,
  useOrders,
  useRefundOrder,
  useShipOrder,
} from "./store-queries";

type Filter = "open" | OrderStatus | "all";

function when(iso: string | null): string {
  return iso === null
    ? ""
    : new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function itemsSummary(o: OrderRow): string {
  return o.items.map((i) => `${String(i.quantity)} × ${i.product} (${i.variant})`).join(", ");
}

/** Store → Orders (ADR 0047): what members bought, to hand over at church or ship, refund, or cancel. */
export function OrdersPage() {
  const [filter, setFilter] = useState<Filter>("open");
  const orders = useOrders(filter);
  const [open, setOpen] = useState<OrderRow | null>(null);
  // The order's dialog unmounts on close, so focus is handed back to the order's button by hand (#261).
  const orderFocus = useReturnFocus();
  const openOrder = (o: OrderRow) => {
    orderFocus.remember();
    setOpen(o);
  };

  return (
    <>
      <PageHeader
        title="Store"
        description="What members can buy in the portal, and the orders to hand over or ship."
      />
      <StoreTabs active="orders" />
      <SegmentedControl
        aria-label="Show orders"
        mb="md"
        value={filter}
        onChange={(value) => {
          setFilter(value as Filter);
        }}
        data={[
          { value: "open", label: "To do" },
          { value: "fulfilled", label: "Done" },
          { value: "cancelled", label: "Cancelled" },
          { value: "all", label: "All" },
        ]}
      />
      {orders.isPending ? (
        <LoadingState rows={4} />
      ) : orders.isError ? (
        <ErrorState message={orders.error.message} onRetry={() => void orders.refetch()} />
      ) : orders.data.length === 0 ? (
        <EmptyState
          title={filter === "open" ? "Nothing to hand over or ship" : "No orders here"}
          description="Orders appear when members buy in the portal's Shop."
        />
      ) : (
        <Table.ScrollContainer minWidth={720}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Order</Table.Th>
                <Table.Th>For</Table.Th>
                <Table.Th>Items</Table.Th>
                <Table.Th className="how-numeric">Total</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {orders.data.map((o) => (
                <Table.Tr
                  key={o.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    openOrder(o);
                  }}
                >
                  <Table.Td>
                    {/* A row answers a mouse; the number is the button a keyboard reaches (#261). */}
                    <UnstyledButton
                      aria-label={`Open order #${String(o.number)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        openOrder(o);
                      }}
                    >
                      <Text size="sm" fw={500}>
                        #{o.number}
                      </Text>
                    </UnstyledButton>
                    <Text size="xs" c="dimmed">
                      {when(o.createdAt)}
                    </Text>
                  </Table.Td>
                  <Table.Td>{o.personName}</Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={2}>
                      {itemsSummary(o)}
                    </Text>
                    {o.forEvent === null ? null : (
                      <Text size="xs" c="dimmed">
                        Add-ons for {o.forEvent.title},{" "}
                        {formatWhen(o.forEvent.startsAt, o.forEvent.timeZone)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td className="how-numeric">
                    {formatMoney(chargedCents(o), o.currency)}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Badge
                        variant="light"
                        color={ORDER_STATUS_COLOR[o.status]}
                        leftSection={
                          o.fulfilment === "ship" ? <IconTruck size={12} aria-hidden /> : null
                        }
                      >
                        {orderStatusLabel(o.status, o.fulfilment)}
                      </Badge>
                      {o.refundedCents > 0 ? (
                        <Badge variant="light" color="gray">
                          {o.refundedCents >= chargedCents(o)
                            ? "Refunded"
                            : `${formatMoney(o.refundedCents, o.currency)} refunded`}
                        </Badge>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {open === null ? null : (
        <OrderModal
          order={open}
          onClose={() => {
            setOpen(null);
            orderFocus.restore();
          }}
        />
      )}
    </>
  );
}

function OrderModal({
  order,
  onClose,
}: {
  readonly order: OrderRow;
  readonly onClose: () => void;
}) {
  const canFulfil = usePermission("commerce.fulfill_orders", { module: "commerce", write: true });
  const canRefund = usePermission("commerce.refund_orders", { module: "commerce", write: true });
  const fulfil = useFulfilOrder();
  const cancel = useCancelOrder();
  const refund = useRefundOrder();
  const ship = useShipOrder();
  const [refunding, setRefunding] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [shipping, setShipping] = useState(false);
  // "Mark shipped…" and "Change tracking…" leave the dialog when pressed, so focus moves to the carrier, and back to
  // the button when the form is put away, never to the page (#261).
  const carrierRef = useRef<HTMLInputElement>(null);
  const shipButtonRef = useRef<HTMLButtonElement>(null);
  const wasShipping = useRef(false);
  useEffect(() => {
    if (shipping) carrierRef.current?.focus();
    else if (wasShipping.current) shipButtonRef.current?.focus();
    wasShipping.current = shipping;
  }, [shipping]);
  const [carrier, setCarrier] = useState(order.carrier ?? "USPS");
  const [tracking, setTracking] = useState(order.trackingNumber ?? "");
  const error = fulfil.error ?? cancel.error ?? ship.error;
  const paid = order.status === "paid" || order.status === "fulfilled";
  const charged = chargedCents(order);
  const shipped = order.fulfilment === "ship";
  const link = trackingUrl(order.carrier, order.trackingNumber);

  return (
    <>
      <Modal
        opened={!refunding && !undoing}
        onClose={onClose}
        title={`Order #${String(order.number)}`}
      >
        <Stack gap="sm">
          {order.forEvent === null ? null : (
            <Text size="sm" c="dimmed">
              Add-ons for {order.forEvent.title},{" "}
              {formatWhen(order.forEvent.startsAt, order.forEvent.timeZone)}; handed over at the
              event.
            </Text>
          )}
          <Group justify="space-between">
            <Text size="sm">
              {order.personName}
              {order.guestEmail === null ? null : (
                <>
                  {" · "}
                  <Anchor size="sm" href={`mailto:${order.guestEmail}`}>
                    {order.guestEmail}
                  </Anchor>
                </>
              )}
            </Text>
            <Badge variant="light" color={ORDER_STATUS_COLOR[order.status]}>
              {orderStatusLabel(order.status, order.fulfilment)}
            </Badge>
          </Group>
          <Table withRowBorders={false} verticalSpacing={4}>
            <Table.Tbody>
              {order.items.map((i, n) => (
                <Table.Tr key={n}>
                  <Table.Td>
                    {i.quantity} × {i.product} ({i.variant})
                  </Table.Td>
                  <Table.Td className="how-numeric">
                    {formatMoney(i.unitPriceCents * i.quantity, order.currency)}
                  </Table.Td>
                </Table.Tr>
              ))}
              {shipped ? (
                <Table.Tr>
                  <Table.Td>Shipping</Table.Td>
                  <Table.Td className="how-numeric">
                    {order.shippingCents === 0
                      ? "Free"
                      : formatMoney(order.shippingCents, order.currency)}
                  </Table.Td>
                </Table.Tr>
              ) : null}
              {order.taxCents > 0 ? (
                <Table.Tr>
                  <Table.Td>Sales tax</Table.Td>
                  <Table.Td className="how-numeric">
                    {formatMoney(order.taxCents, order.currency)}
                  </Table.Td>
                </Table.Tr>
              ) : null}
              <Table.Tr>
                <Table.Td fw={600}>Total</Table.Td>
                <Table.Td className="how-numeric" fw={600}>
                  {formatMoney(charged, order.currency)}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          {shipped ? (
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                Ship to
              </Text>
              {order.shipTo === null ? (
                <Text size="sm" c="dimmed">
                  {order.status === "pending_payment"
                    ? "The address comes with the payment."
                    : "Stripe sent no address with this payment."}
                </Text>
              ) : (
                addressLines(order.shipTo).map((line, n) => (
                  <Text size="sm" key={n}>
                    {line}
                  </Text>
                ))
              )}
              {order.status === "fulfilled" ? (
                <Text size="sm" mt={4}>
                  {order.carrier ?? "Carrier not given"}
                  {order.trackingNumber === null ? "" : " · "}
                  {link === null ? (
                    order.trackingNumber
                  ) : (
                    <Anchor
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      // The visible number first, then what the link does (SC 2.5.3, UI_STANDARDS; #277).
                      aria-label={`${order.trackingNumber ?? ""}: track with ${order.carrier ?? "the carrier"} (opens in a new tab)`}
                    >
                      {order.trackingNumber}
                    </Anchor>
                  )}
                </Text>
              ) : null}
            </Stack>
          ) : null}
          <Text size="xs" c="dimmed">
            Placed {when(order.createdAt)}
            {order.paidAt === null ? "" : ` · paid ${when(order.paidAt)}`}
            {order.fulfilledAt === null
              ? ""
              : ` · ${shipped ? "shipped" : "picked up"} ${when(order.fulfilledAt)}`}
            {order.cancelReason === null ? "" : ` · ${order.cancelReason}`}
            {order.refundedCents > 0
              ? ` · ${formatMoney(order.refundedCents, order.currency)} refunded`
              : ""}
          </Text>
          {shipping ? (
            <Stack gap="xs">
              <Group grow align="flex-start">
                <Autocomplete
                  ref={carrierRef}
                  label="Carrier"
                  data={[...CARRIERS]}
                  maxLength={60}
                  value={carrier}
                  onChange={setCarrier}
                />
                <TextInput
                  label="Tracking number"
                  description="Optional. The member sees it with a link."
                  maxLength={100}
                  value={tracking}
                  onChange={(event) => {
                    setTracking(event.currentTarget.value);
                  }}
                />
              </Group>
            </Stack>
          ) : null}
          {error ? (
            <Alert color="red" variant="light">
              {error.message}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            {order.status === "pending_payment" && canFulfil.data === true ? (
              <Button
                variant="subtle"
                color="red"
                loading={cancel.isPending}
                onClick={() => {
                  cancel.mutate(order.id, { onSuccess: onClose });
                }}
              >
                Cancel order
              </Button>
            ) : null}
            {paid && order.refundedCents < charged && canRefund.data === true && !shipping ? (
              <Button
                variant="subtle"
                onClick={() => {
                  setRefunding(true);
                }}
              >
                Refund…
              </Button>
            ) : null}
            {order.status === "fulfilled" && !shipped && canFulfil.data === true ? (
              <Button
                variant="subtle"
                onClick={() => {
                  setUndoing(true);
                }}
              >
                Undo pickup
              </Button>
            ) : null}
            {order.status === "paid" && !shipped && canFulfil.data === true ? (
              <Button
                loading={fulfil.isPending}
                onClick={() => {
                  fulfil.mutate(order.id, { onSuccess: onClose });
                }}
              >
                Mark picked up
              </Button>
            ) : null}
            {shipped && paid && canFulfil.data === true && !shipping ? (
              <Button
                ref={shipButtonRef}
                variant={order.status === "paid" ? "filled" : "subtle"}
                onClick={() => {
                  setShipping(true);
                }}
              >
                {order.status === "paid" ? "Mark shipped…" : "Change tracking…"}
              </Button>
            ) : null}
            {shipping ? (
              <>
                <Button
                  variant="subtle"
                  onClick={() => {
                    setShipping(false);
                  }}
                >
                  Back
                </Button>
                <Button
                  loading={ship.isPending}
                  onClick={() => {
                    ship.mutate(
                      { orderId: order.id, carrier, trackingNumber: tracking },
                      { onSuccess: onClose },
                    );
                  }}
                >
                  {order.status === "paid" ? "Mark shipped" : "Save tracking"}
                </Button>
              </>
            ) : null}
          </Group>
        </Stack>
      </Modal>
      {refunding ? (
        <RefundDialog
          title={`Refund order #${String(order.number)}`}
          payer={order.personName}
          amountCents={charged}
          refundedCents={order.refundedCents}
          currency={order.currency}
          pending={refund.isPending}
          error={refund.error}
          onRefund={(amountCents, reason) => {
            refund.mutate({ orderId: order.id, amountCents, reason }, { onSuccess: onClose });
          }}
          onClose={() => {
            setRefunding(false);
          }}
        />
      ) : null}
      {undoing ? (
        <UndoPickupDialog
          title="Undo pickup"
          orderId={order.id}
          orderNumber={order.number}
          onClose={() => {
            setUndoing(false);
          }}
          onUndone={onClose}
        />
      ) : null}
    </>
  );
}
